import React from 'react';

import styled from '@emotion/styled';

import { Box, COLORS, FontWeight, Text, mq } from '@clutter/clean';

import { RateGroupKindEnum } from '@graphql/platform';

import { Commitment as CommitmentType } from '@root/resources/types/commitment';

import {
  PricingSummary,
  roundedDiscountPercentage,
} from '@utils/hooks/pricing/use_pricing_summary';

import { RadioCard } from '../../../subcomponents/radio_card';

const StyledRadioCard = styled(RadioCard)`
  & > div {
    height: 100%;
  }
`;

const CommitmentCards = styled.div`
  display: grid;
  ${mq({
    gridTemplateColumns: ['1fr', null, '1fr 1fr 1fr'],
    gridGap: ['10px', null, '16px'],
  })}
`;

const CommitmentCardContents: React.FC<{
  commitment: RateGroupKindEnum;
  summary?: PricingSummary;
  defaultPercent?: number;
  titleText: React.ReactNode;
  description?: React.ReactNode;
}> = ({ commitment, summary, defaultPercent, titleText, description }) => {
  const discountedPrice =
    commitment === RateGroupKindEnum.Saver
      ? summary?.saverMonthlyAmount
      : commitment === RateGroupKindEnum.Flexer
        ? summary?.flexerMonthlyAmount
        : undefined;

  const discountText =
    commitment === RateGroupKindEnum.Mover
      ? 'Most flexible'
      : `${
          summary && discountedPrice
            ? roundedDiscountPercentage(
                summary.moverMonthlyAmount,
                discountedPrice,
              )
            : defaultPercent
        }% discount`;

  return (
    <>
      {!description && (
        <Box.Flex
          textAlign="center"
          flexDirection="column"
          justifyContent="center"
          height="100%"
          padding="0 4px"
        >
          <Text.Title size="extraSmall">{titleText}</Text.Title>
          <Box
            padding="2px 4px"
            margin="0 auto"
            background={
              commitment === RateGroupKindEnum.Mover
                ? 'transparent'
                : COLORS.dust
            }
            borderRadius="4px"
          >
            <Text.Caption weight={FontWeight.Medium} color={COLORS.moss}>
              {discountText}
            </Text.Caption>
          </Box>
        </Box.Flex>
      )}
      {description && (
        <Box.Flex
          textAlign="center"
          flexDirection="column"
          justifyContent={['space-evenly', undefined]}
          height="100%"
          padding="0 4px"
        >
          <Box.FlexItem margin="0 0 8px">
            <Text.Title size="extraSmall">{titleText}</Text.Title>
          </Box.FlexItem>
          <Box textAlign={['center', null, null, 'left']}>
            <Text.Callout color={COLORS.storm}>{description}</Text.Callout>
          </Box>
        </Box.Flex>
      )}
    </>
  );
};

export const Cards: React.FC<{
  commitment?: CommitmentType;
  pricingSummary?: PricingSummary;
  defaultPricingSummary?: PricingSummary;
  onChange: (commitment: CommitmentType) => void;
  scrollToStep: () => void;
}> = ({ commitment, pricingSummary, defaultPricingSummary, onChange }) => {
  const flexerDefaultPercent = defaultPricingSummary
    ? roundedDiscountPercentage(
        defaultPricingSummary.moverMonthlyAmount,
        defaultPricingSummary.flexerMonthlyAmount,
      )
    : 20;

  const saverDefaultPercent = defaultPricingSummary
    ? roundedDiscountPercentage(
        defaultPricingSummary.moverMonthlyAmount,
        defaultPricingSummary.saverMonthlyAmount,
      )
    : 40;

  const moverCard = (
    <StyledRadioCard
      selected={commitment === RateGroupKindEnum.Mover}
      onChange={() => onChange(RateGroupKindEnum.Mover)}
      name="commitment"
      value="mover"
    >
      <CommitmentCardContents
        commitment={RateGroupKindEnum.Mover}
        summary={pricingSummary}
        titleText="Month to month"
        description={<>Additional pickups and deliveries can be purchased.</>}
      />
    </StyledRadioCard>
  );

  const flexerCard = (
    <StyledRadioCard
      selected={commitment === RateGroupKindEnum.Flexer}
      onChange={() => onChange(RateGroupKindEnum.Flexer)}
      name="commitment"
      value="flexer"
    >
      <CommitmentCardContents
        commitment={RateGroupKindEnum.Flexer}
        summary={pricingSummary}
        defaultPercent={flexerDefaultPercent}
        titleText={
          <>
            4 <span style={{ whiteSpace: 'nowrap' }}>months +</span>
          </>
        }
        description={
          <>
            Get <b>one</b> free additional pickup or delivery.
          </>
        }
      />
    </StyledRadioCard>
  );

  const saverCard = (
    <StyledRadioCard
      selected={commitment === RateGroupKindEnum.Saver}
      onChange={() => onChange(RateGroupKindEnum.Saver)}
      name="commitment"
      value="saver"
    >
      <CommitmentCardContents
        commitment={RateGroupKindEnum.Saver}
        summary={pricingSummary}
        defaultPercent={saverDefaultPercent}
        titleText={
          <>
            8 <span style={{ whiteSpace: 'nowrap' }}>months +</span>
          </>
        }
        description={
          <>
            Get <b>four</b> free additional pickups or deliveries.
          </>
        }
      />
    </StyledRadioCard>
  );
  return (
    <CommitmentCards>
      {moverCard}
      {flexerCard}
      {saverCard}
    </CommitmentCards>
  );
};
